/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {
    Stack,
    Box,
    Button,
    Flex,
    useMultiStyleConfig,
    Checkbox,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {IconButton} from '@chakra-ui/react'
import {FormattedMessage, useIntl} from 'react-intl'
import {CloseIcon, CheckIcon} from '../icons'

/**
 * Renders a card-style box with optional edit and remove buttons. Used for
 * lists of addresses, payment methods, or any other list-type content.
 * The provided `onRemove` callback triggers a loading spinner internally
 * if given a promise.
 */
const ActionCard = ({
    children,
    onEdit,
    onRemove,
    editBtnRef,
    editBtnLabel,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    removeBtnLabel,
    variant,
    onSetDefault,
    isDefault,
    isAddressSection,
    dataTestid,
    ...props
}) => {
    const styles = useMultiStyleConfig('ActionCard', {variant})
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
    const [showLoading, setShowLoading] = useState(false)

    const intl = useIntl()

    const handleRemove = async () => {
        setShowLoading(true)
        try {
            return await Promise.resolve(onRemove())
        } finally {
            setShowLoading(false)
        }
    }

    const showDefaultCheckbox = variant !== 'checkout' && variant !== 'boxed'

    return (
        <Box
            position="relative"
            {...props}
            {...styles.actionCardBox}
            data-testid={dataTestid ? dataTestid : 'action-card'}
        >
            <Stack {...styles.actionCardStack}>
                <Flex {...styles.childrenFlex}>{children}</Flex>
                <Stack direction="row" alignItems="center" justifyContent={'space-between'}>
                    {showDefaultCheckbox && (
                        <Checkbox
                            isChecked={isDefault}
                            icon={isDefault ? <CheckIcon /> : <></>}
                            onChange={onSetDefault}
                            {...styles.checkboxDefault}
                            aria-label="Set as default address"
                            sx={
                                isAddressSection && isDefault
                                    ? {
                                          pointerEvents: 'none',
                                      }
                                    : {}
                            }
                        >
                            <FormattedMessage
                                defaultMessage="Default"
                                id="action_card.checkbox.default"
                            />
                        </Checkbox>
                    )}
                    {onEdit && (
                        <Button
                            onClick={onEdit}
                            variant="text-button"
                            size="sm"
                            ref={editBtnRef}
                            aria-label={editBtnLabel}
                            padding={0}
                            {...styles.editPosition}
                            data-testid="action-card-edit"
                        >
                            <FormattedMessage defaultMessage="Edit" id="action_card.action.edit" />
                        </Button>
                    )}
                    {onRemove && (
                        <IconButton
                            {...styles.closeButton}
                            icon={<CloseIcon {...styles.closeIcon} />}
                            onClick={handleRemove}
                            aria-label={intl.formatMessage({
                                id: 'global .close_icon.aria_label',
                                defaultMessage: 'Close Icon',
                            })}
                        />
                    )}
                </Stack>
            </Stack>
        </Box>
    )
}

ActionCard.propTypes = {
    /** Callback fired on edit */
    onEdit: PropTypes.func,

    /** Callback fired on remove click (if promise, will toggle loading spinner) */
    onRemove: PropTypes.func,

    /** Content rendered in card */
    children: PropTypes.node,

    /** Ref for the edit button so that it can be focused on for accessibility */
    editBtnRef: PropTypes.object,

    /** Accessibility label for edit button */
    editBtnLabel: PropTypes.string,

    /** Accessibility label for remove button */
    removeBtnLabel: PropTypes.string,

    /** Variant of the action card */
    variant: PropTypes.string,

    onSetDefault: PropTypes.func,

    isDefault: PropTypes.bool,

    /** Determines if the card is in the address section */
    isAddressSection: PropTypes.bool,

    dataTestid: PropTypes.string,
}

export default ActionCard
